import React from "react";
import { useEffect, useState } from "react";
import Header from "components/Header";
import { cardIconColor } from "components/Header";
import {
    FaUserCheck,
    FaHeartbeat,
    FaDiagnoses,
    FaVial,
    FaPills,
    FaExclamationTriangle,
    FaHospital,
    FaUserNurse,
    FaBaby,
    FaWeight,
    FaCalendarCheck,
    FaClipboardList,
    FaSyringe,
    FaTint,
    FaMedkit,
    FaHome,
    FaCheckCircle,
    FaBuilding,
    FaShieldAlt,
    FaAppleAlt,
    FaTooth,
    FaEye,
    FaStethoscope,
    FaInfoCircle,
    FaChild,
    FaFemale,
    FaBabyCarriage,
    FaClipboardCheck,

} from "react-icons/fa";
import { color } from "echarts";

// Single-file React page (not reusable components) using Tailwind for styling
// Argon-like visual language: soft cards, rounded, shadows, subtle gradients

const formatValue = (v) => v;

// helper to render trend: expects a numeric change (positive or negative)
const Trend = ({ change }) => {
    if (change === null || change === undefined) return null;
    const isUp = Number(change) >= 0;
    const abs = Math.abs(Number(change));
    return (
        <div className="flex items-center justify-end gap-2 text-xs mt-2 px-2">
            <span className={isUp ? "text-green-500 font-semibold" : "text-red-500 font-semibold"}>
                {isUp ? "▲" : "▼"} {abs}%
            </span>
            <span className="text-gray-400">from last Month</span>
        </div>
    );
};
const TrendV1 = ({ change }) => {
    if (change === null || change === undefined) return null;

    const isUp = Number(change) >= 0;
    const abs = Math.abs(Number(change));

    return (
        <div className="flex items-center justify-end gap-1 text-xs mt-2 px-3 relative">

            {/* Trend Arrow + Value */}
            <span className={isUp ? "text-green-500 font-semibold" : "text-red-500 font-semibold"}>
                {isUp ? "▲" : "▼"} {abs}%
            </span>

            {/* Tooltip Wrapper */}
            <div className="relative group inline-flex items-center cursor-pointer">
                <FaInfoCircle className="text-gray-400 hover:text-gray-600" size={12} />

                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 
                                hidden group-hover:block
                                bg-slate-800 text-white text-[10px] 
                                rounded-md px-2 py-1 shadow-lg 
                                whitespace-nowrap z-20">
                    Ratio of Current to Previous Month
                </div>
            </div>
        </div>
    );
};

export default function RmnchDashboardPage() {

    // Data: every item has title, subtitle (long title), value, change (percent change number), color, icon

    const anc = [
        {
            icon: <FaUserCheck className="w-6 h-6" />,
            short: "Early ANC",
            title: "Early ANC Registration (1st Trimester)",
            value: "78%",
            change: 3,
            color: cardIconColor// "bg-gradient-to-br from-blue-50 to-blue-100"
        },
        {
            icon: <FaHeartbeat className="w-6 h-6" />,
            short: "4+ ANC",
            title: "4 or More ANC Visits",
            value: "92%",
            change: -1,
            color: cardIconColor// "bg-gradient-to-br from-red-50 to-red-100"
        },
        {
            icon: <FaDiagnoses className="w-6 h-6" />,
            short: "Full ANC",
            title: "Full ANC Coverage",
            value: "68%",
            change: 2,
            color: cardIconColor// "bg-gradient-to-br from-violet-50 to-violet-100"
        },
        {
            icon: <FaVial className="w-6 h-6" />,
            short: "Anaemia",
            title: "Anaemia Screening Coverage",
            value: "84%",
            change: 1.5,
            color: cardIconColor// "bg-gradient-to-br from-cyan-50 to-cyan-100"
        },
        {
            icon: <FaPills className="w-6 h-6" />,
            short: "IFA",
            title: "IFA Consumption (180+ Tablets)",
            value: "61%",
            change: 4,
            color: cardIconColor// "bg-gradient-to-br from-emerald-50 to-emerald-100"
        },
        {
            icon: <FaExclamationTriangle className="w-6 h-6" />,
            short: "HRP",
            title: "High-Risk Pregnancy Identified",
            value: "12%",
            change: -0.5,
            color: cardIconColor// "bg-gradient-to-br from-amber-50 to-amber-100"
        }
    ];
    const MaternalANC = [
        {
            icon: <FaUserCheck className="w-6 h-6" />,
            short: "ANC Reg",
            title: "ANC Trimester (1st / 2nd / 3rd)",
            value: "233 / 342 / 323",
            color: "bg-[#428bab]"
            // color: cardIconColor// "bg-gradient-to-br from-blue-50 to-blue-100"
        },
        {
            icon: <FaHeartbeat className="w-6 h-6" />,
            short: "HRP",
            title: "High Risk Pregnancy",
            value: <span className="text-red-600 font-semibold">954</span>,
            color: "bg-[#428bab]"
            // color: cardIconColor// "bg-gradient-to-br from-red-50 to-red-100"
        },
        {
            icon: <FaPills className="w-6 h-6" />,
            short: "IFA",
            title: "IFA Distribution (180+ Tablets)",
            value: "6,122",
            color: "bg-[#428bab]"
            // color: cardIconColor// "bg-gradient-to-br from-violet-50 to-violet-100"
        },
        {
            icon: <FaVial className="w-6 h-6" />,
            short: "Anaemia",
            title: (
                <span>
                    Anemia (
                    <span className="text-red-600 font-semibold">Severe</span> /
                    <span className="text-blue-600 font-semibold"> Moderate</span> /
                    <span className="text-green-600 font-semibold"> Normal</span>
                    )
                </span>
            ),
            value: (
                <span>
                    <span className="text-red-600 font-semibold">843</span> /
                    <span className="text-blue-600 font-semibold"> 433</span> /
                    <span className="text-green-600 font-semibold"> 322</span>
                </span>
            ),
            color: "bg-[#428bab]"
        },
        {
            icon: <FaHome className="w-6 h-6" />,
            short: "PNC 48hr",
            title: "PNC Visit within 48 Hours",
            value: "389",
            color: "bg-[#428bab]"
            // color: cardIconColor// "bg-gradient-to-br from-indigo-50 to-indigo-100"
        },
        {
            icon: <FaClipboardCheck className="w-6 h-6" />,
            short: "PNC",
            title: "PNC Visit Completion Rate",
            value: "506 / 697",
            color: "bg-[#428bab]"
            // color: cardIconColor// "bg-gradient-to-br from-green-50 to-green-100"
        }
    ];

    const maternal = [
        {
            icon: <FaHospital className="w-6 h-6" />,
            short: "Inst. Delivery",
            title: "Institutional Delivery Rate",
            value: "92%",
            change: 1.2,
            color: cardIconColor// "bg-gradient-to-br from-blue-50 to-blue-100"
        },
        {
            icon: <FaUserNurse className="w-6 h-6" />,
            short: "SBA",
            title: "Skilled Birth Attendance",
            value: "89%",
            change: 0.8,
            color: cardIconColor// "bg-gradient-to-br from-cyan-50 to-cyan-100"
        },
        {
            icon: <FaBaby className="w-6 h-6" />,
            short: "C-Section",
            title: "Caesarean Section Rate",
            value: "28%",
            change: 2,
            color: cardIconColor// "bg-gradient-to-br from-purple-50 to-purple-100"
        },
        {
            icon: <FaHeartbeat className="w-6 h-6" />,
            short: "PNC 48H",
            title: "PNC Check-up within 48 Hours",
            value: "76%",
            change: 3.5,
            color: cardIconColor// "bg-gradient-to-br from-emerald-50 to-emerald-100"
        },
        {
            icon: <FaExclamationTriangle className="w-6 h-6" />,
            short: "MMR",
            title: "Maternal Mortality Ratio (MMR per 100000)",
            value:  <span className="text-red-600 font-semibold">47</span>,
            change: -5,
            color: cardIconColor// "bg-gradient-to-br from-red-50 to-red-100"
        },
        {
            icon: <FaWeight className="w-6 h-6" />,
            short: "LBW",
            title: "Low Birth Weight (%)",
            value: "14%",
            change: -0.5,
            color: cardIconColor// "bg-gradient-to-br from-amber-50 to-amber-100"
        }
    ];

    const pncHbnc = [
        {
            icon: <FaUserNurse className="w-6 h-6" />,
            short: "PNC 48H",
            title: "PNC Check-up within 48 Hours",
            value: "76%",
            change: 3.5,
            color: cardIconColor// "bg-gradient-to-br from-blue-50 to-blue-100"
        },
        {
            icon: <FaHeartbeat className="w-6 h-6" />,
            short: "PNC 7D",
            title: "PNC Check-up within 7 Days",
            value: "82%",
            change: 2,
            color: cardIconColor// "bg-gradient-to-br from-cyan-50 to-cyan-100"
        },
        {
            icon: <FaHome className="w-6 h-6" />,
            short: "HBNC",
            title: "Newborn Home Visits (HBNC)",
            value: "88%",
            change: 1,
            color: cardIconColor// "bg-gradient-to-br from-emerald-50 to-emerald-100"
        },
        {
            icon: <FaBaby className="w-6 h-6" />,
            short: "Early BF",
            title: "Early Initiation of Breastfeeding",
            value: "79%",
            change: 2.2,
            color: cardIconColor// "bg-gradient-to-br from-amber-50 to-amber-100"
        },
        {
            icon: <FaCheckCircle className="w-6 h-6" />,
            short: "Danger",
            title: "Newborn Referred for Danger Signs",
            value: "6%",
            change: -0.2,
            color: cardIconColor// "bg-gradient-to-br from-red-50 to-red-100"
        },
        {
            icon: <FaUserNurse className="w-6 h-6" />,
            short: "PNC 6W",
            title: "PNC Check-up within 6 Weeks",
            value: "68%",
            change: 1,
            color: cardIconColor// "bg-gradient-to-br from-violet-50 to-violet-100"
        }
    ];

    const childUnder5 = [
        {
            icon: <FaSyringe className="w-6 h-6" />,
            short: "Full Immun.",
            title: "Fully Immunized Children",
            value: "912",
            change: 0.5,
            color: cardIconColor// "bg-gradient-to-br from-blue-50 to-blue-100"
        },
        {
            icon: <FaTint className="w-6 h-6" />,
            short: "Vit A",
            title: "Vitamin A Supplementation",
            value: "851",
            change: 1,
            color: cardIconColor// "bg-gradient-to-br from-cyan-50 to-cyan-100"
        },
        {
            icon: <FaHeartbeat className="w-6 h-6" />,
            short: "NMR",
            title: "Neonatal Mortality Rate (per 1000)",
            value:  <span className="text-red-600 font-semibold">11</span>,
            change: -0.3,
            color: cardIconColor// "bg-gradient-to-br from-red-50 to-red-100"
        },
        {
            icon: <FaHospital className="w-6 h-6" />,
            short: "IMR",
            title: "Infant Mortality Rate (per 1000)",
            value:  <span className="text-red-600 font-semibold">16</span>,
            change: -0.2,
            color: cardIconColor// "bg-gradient-to-br from-purple-50 to-purple-100"
        },
        // {
        //     icon: <FaBaby className="w-6 h-6" />,
        //     short: "LBW",
        //     title: "Low Birth Weight Children (%)",
        //     value: "141",
        //     change: -0.5,
        //     color: cardIconColor// "bg-gradient-to-br from-amber-50 to-amber-100"
        // },
        {
            icon: <FaMedkit className="w-6 h-6" />,
            short: "SNCU",
            title: "SNCU Admissions",
            value: "320",
            change: 2,
            color: cardIconColor// "bg-gradient-to-br from-emerald-50 to-emerald-100"
        },
        // {
        //     icon: <FaBabyCarriage className="w-6 h-6" />,
        //     short: "NMR",
        //     title: "Neonatal Mortality Rate (NMR)",
        //     value:  <span className="text-red-600 font-semibold">9</span>,
        //     color: "bg-[#428bab]"
        //     // color: cardIconColor// "bg-gradient-to-br from-teal-50 to-teal-100"
        // },
        // {
        //     icon: <FaChild className="w-6 h-6" />,
        //     short: "IMR",
        //     title: "Infant Mortality Rate (IMR)",
        //     value:  <span className="text-red-600 font-semibold">14</span>,
        //     color: "bg-[#428bab]"
        //     // color: cardIconColor// "bg-gradient-to-br from-red-50 to-red-100"
        // },
    ];

    const childAbove5 = [
        {
            icon: <FaWeight className="w-6 h-6" />,
            short: "Stunting",
            title: "Stunted Children (%)",
            value: "18%",
            change: -0.4,
            color: cardIconColor// "bg-gradient-to-br from-red-50 to-red-100"
        },
        {
            icon: <FaAppleAlt className="w-6 h-6" />,
            short: "Wasting",
            title: "Wasted Children (%)",
            value: "8%",
            change: -0.2,
            color: cardIconColor// "bg-gradient-to-br from-amber-50 to-amber-100"
        },
        {
            icon: <FaHeartbeat className="w-6 h-6" />,
            short: "Anemia",
            title: "Children with Anemia (%)",
            value: "32%",
            change: -0.5,
            color: cardIconColor// "bg-gradient-to-br from-blue-50 to-blue-100"
        },
        {
            icon: <FaTooth className="w-6 h-6" />,
            short: "Dental",
            title: "Dental Check-up Coverage (%)",
            value: "65%",
            change: 1,
            color: cardIconColor// "bg-gradient-to-br from-cyan-50 to-cyan-100"
        },
        {
            icon: <FaEye className="w-6 h-6" />,
            short: "Vision",
            title: "Vision Screening (%)",
            value: "70%",
            change: 2,
            color: cardIconColor// "bg-gradient-to-br from-purple-50 to-purple-100"
        },
        {
            icon: <FaStethoscope className="w-6 h-6" />,
            short: "School Health",
            title: "School Health Check-up Coverage (%)",
            value: "80%",
            change: 1.5,
            color: cardIconColor// "bg-gradient-to-br from-emerald-50 to-emerald-100"
        }
    ];

    const ncd = [
        {
            icon: <FaUserCheck className="w-6 h-6" />,
            short: "Screening",
            title: "Total Population Screened (30+)",
            value: "3.42 Cr",
            change: 5,
            color: cardIconColor// "bg-gradient-to-br from-blue-50 to-blue-100"
        },
        {
            icon: <FaHeartbeat className="w-6 h-6" />,
            short: "HTN+",
            title: "Hypertension Positivity Rate",
            value: "322",
            change: -0.8,
            color: cardIconColor// "bg-gradient-to-br from-red-50 to-red-100"
        },
        {
            icon: <FaTint className="w-6 h-6" />,
            short: "DM+",
            title: "Diabetes Positivity Rate",
            value: "832",
            change: -0.4,
            color: cardIconColor// "bg-gradient-to-br from-cyan-50 to-cyan-100"
        },
        {
            icon: <FaDiagnoses className="w-6 h-6" />,
            short: "Cancer",
            title: "Cancer Screening Coverage",
            value: "190",
            change: 1.2,
            color: cardIconColor// "bg-gradient-to-br from-purple-50 to-purple-100"
        },
        {
            icon: <FaCheckCircle className="w-6 h-6" />,
            short: "HTN Ctrl",
            title: "Hypertension Control Rate",
            value: "483",
            change: 2.5,
            color: cardIconColor// "bg-gradient-to-br from-emerald-50 to-emerald-100"
        },
        {
            icon: <FaCheckCircle className="w-6 h-6" />,
            short: "DM Ctrl",
            title: "Diabetes Control Rate",
            value: "394",
            change: 1.8,
            color: cardIconColor// "bg-gradient-to-br from-teal-50 to-teal-100"
        }
    ];

    const infra = [
        { icon: <FaBuilding className="w-6 h-6" />, short: "DH", title: "District Hospital", value: "23", color: cardIconColor },
        { icon: <FaBuilding className="w-6 h-6" />, short: "SDH", title: "Sub-District Hospital", value: "95", color: cardIconColor },
        { icon: <FaBuilding className="w-6 h-6" />, short: "RH", title: "Rural Hospital", value: "359", color: cardIconColor },
        { icon: <FaBuilding className="w-6 h-6" />, short: "PHC", title: "Primary Health Centre", value: "1,818", color: cardIconColor },
        { icon: <FaBuilding className="w-6 h-6" />, short: "SC", title: "Sub-Centre", value: "10,580", color: cardIconColor },
        { icon: <FaBuilding className="w-6 h-6" />, short: "MC", title: "Medical College", value: "44", color: cardIconColor }
    ];

    const DeliveryOutcome = [
        {
            icon: <FaHospital className="w-6 h-6" />,
            short: "Delivery",
            title: "Total Delivery",
            value: "802",
            color: "bg-[#428bab]"
            // color: cardIconColor// "bg-gradient-to-br from-emerald-50 to-emerald-100"
        },
        {
            icon: <FaHospital className="w-6 h-6" />,
            short: "Delivery",
            title: "Institutional Delivery / Home Delivery",
            value: "376 / 304",
            color: "bg-[#428bab]"
            // color: cardIconColor// "bg-gradient-to-br from-emerald-50 to-emerald-100"
        },
        {
            icon: <FaHospital className="w-6 h-6" />,
            short: "Delivery",
            title: "Vaginal Delivery / C-Section Delivery",
            value: "276 / 344",
            color: "bg-[#428bab]"
            // color: cardIconColor// "bg-gradient-to-br from-emerald-50 to-emerald-100"
        },
        {
            icon: <FaBaby className="w-6 h-6" />,
            short: "Birth",
            title: "Live Birth",
            value: "123",
            color: "bg-[#428bab]"
            // color: cardIconColor// "bg-gradient-to-br from-amber-50 to-amber-100"
        },
        {
            icon: <FaChild className="w-6 h-6" />,
            short: "LBW",
            title: "Low Birth Weight (LBW)",
            value: <span className="text-red-600 font-semibold">42</span>,
            color: "bg-[#428bab]"
            // color: cardIconColor// "bg-gradient-to-br from-orange-50 to-orange-100"
        },
        {
            icon: <FaFemale className="w-6 h-6" />,
            short: "MMR",
            title: "Maternal Mortality Rate (MMR per 1000)",
            value:  <span className="text-red-600 font-semibold">11</span>,
            color: "bg-[#428bab]"
            // color: cardIconColor// "bg-gradient-to-br from-rose-50 to-rose-100"
        },
    ]

    // Section renderer
    const Section = ({ title, items }) => (
        <section className="mb-8">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-semibold text-slate-700">{title}</h3>
                <div className="text-sm text-slate-500">Last updated: {new Date().toLocaleDateString()}</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {items.map((it, idx) => (
                    <div key={idx} className="bg-white rounded-xl shadow-md p-3 flex flex-col justify-between h-full">
                        <div className="flex items-start gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${it.color || 'bg-slate-100'}`}>
                                {it.icon}
                            </div>
                            <div className="flex-1 min-w-0 text-right">
                                <div className="text-xs text-slate-500 break-words">{it.title}</div>
                                <div className="text-2xl font-bold text-slate-800 leading-snug">{formatValue(it.value)}</div>
                            </div>
                        </div>
                        {typeof it.change !== 'undefined' && it.change !== null && <Trend change={it.change} />}
                    </div>
                ))}
            </div>
        </section>
    );
    const Facilities = ({ title, items }) => (
        <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-semibold text-slate-700">{title}</h3>
                <div className="text-sm text-slate-500">Last updated: {new Date().toLocaleDateString()}</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {items.map((it, idx) => (
                    <div key={idx} className="bg-white rounded-xl shadow-md p-3 flex flex-col justify-between h-full">
                        <div className="flex items-start gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${it.color || 'bg-slate-100'}`}>
                                {it.icon}
                            </div>
                            <div className="flex-1 min-w-0 text-right">
                                <div className="text-xs text-slate-500 break-words">{it.title}</div>
                                <div className="text-2xl font-bold text-slate-800 leading-snug">{formatValue(it.value)}</div>
                            </div>
                        </div>
                        {typeof it.change !== 'undefined' && it.change !== null && <Trend change={it.change} />}
                    </div>
                ))}
            </div>
        </div>
    );
    const date = new Date();                          // today
    date.setDate(date.getDate() - 1);                 // subtract 1 day

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const formatted = `${day}-${month}-${year}`;

    const ANC = ({ title, items }) => (
        <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-semibold text-slate-700">{title}</h3>
                <div className="text-sm text-slate-500">Last updated: {formatted}</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {items.map((it, idx) => (
                    <div key={idx} className="bg-white rounded-xl shadow-md p-3 flex flex-col justify-between h-full">
                        <div className="flex items-start gap-3">
                            <div className={`w-12 h-12 rounded-md flex items-center justify-center text-white text-lg ${it.color || 'bg-slate-100'}`}>
                                {it.icon}
                            </div>

                            <div className="flex-1 min-w-0 text-right">
                                <div className="text-sm text-slate-500 font-medium tracking-wide break-words">
                                    {it.title}
                                </div>
                                <div className="d-flex justify-content-end mt-3">
                                    {typeof it.change !== 'undefined' && it.change !== null && <TrendV1 className="px-2" change={it.change} />}
                                    <div className="text-xl font-bold text-slate-800 leading-snug">{formatValue(it.value)}</div>
                                </div>
                            </div>
                        </div>

                    </div>
                ))}
            </div>
        </div>
    );
    const PNC = ({ title, items }) => (
        <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-semibold text-slate-700">{title}</h3>
                <div className="text-sm text-slate-500">Last updated: {new Date().toLocaleDateString()}</div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {items.map((it, idx) => (
                    <div key={idx} className="bg-white rounded-xl shadow-md p-4 flex flex-col justify-between h-full">
                        <div className="flex items-start gap-3">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${it.color || 'bg-slate-100'}`}>
                                {it.icon}
                            </div>
                            <div className="flex-1 min-w-0 text-right">
                                <div className="text-xs text-slate-500 break-words">{it.title}</div>
                                <div className="d-flex justify-content-end">

                                    <div className="text-2xl font-bold text-slate-800 leading-snug">{formatValue(it.value)}</div>
                                    {typeof it.change !== 'undefined' && it.change !== null && <Trend className="px-2" change={it.change} />}
                                </div>
                            </div>
                        </div>

                    </div>
                ))}
            </div>
        </div>
    );
    const handleMenuClick = () => {
        // alert("Sidebar opened! (You can link this to real sidebar)");
    };

    const [searchQuery, setSearchQuery] = useState("");
    const [selected, setSelected] = useState("");
    return (
        <div className="container-fluid">
            <Header
                onMenuClick={handleMenuClick}
                selected={selected}
                setSelected={setSelected}
                onSearchChange={setSearchQuery}
                dashboardName="Maharashtra State Health Dashboard"
            />
            <div className="min-h-screen bg-slate-50 p-6">
                {/* <div className="max-w-7xl mx-auto"> */}
                {/* Header */}
                {/* <header className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-extrabold text-slate-800">RMNCH & NCD Dashboard</h1>
                        <p className="text-sm text-slate-500 mt-1">Comprehensive state-level dashboard covering ANC, Maternal, PNC/HBNC, Child Health, NCDs and Infrastructure</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button className="px-4 py-2 bg-white border rounded-lg shadow-sm text-sm">Export</button>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm text-sm">Share</button>
                    </div>
                </header> */}

                {/* Sections */}
                <ANC title="Health Infrastructure" items={infra} />
                <ANC title="Antenatal Care (ANC)" items={MaternalANC} />
                {/* <ANC title="Postnatal Care & HBNC" items={pncHbnc} /> */}
                <ANC title="Delivery Outcome" items={DeliveryOutcome} />
                <ANC title="Child Health" items={childUnder5} />
                {/* <ANC title="Child Health (5+ years)" items={childAbove5} /> */}
                <ANC title="Non-Communicable Diseases (NCD)" items={ncd} />


                {/* <footer className="mt-12 text-center text-sm text-slate-400">Designed with Argon-style visuals • Tailwind CSS</footer> */}
                {/* </div> */}
            </div>
        </div>
    );
}
